import { Item } from '@radix-ui/react-dropdown-menu'
import { cn } from '@seen/ui'
import {
  type ComponentPropsWithoutRef,
  type ElementRef,
  forwardRef,
  type ReactNode,
} from 'react'

type ItemProps = ComponentPropsWithoutRef<typeof Item>

export interface DropdownMenuItemProps extends ItemProps {
  asChild?: boolean // default false
  disabled?: boolean
  loading?: boolean
  onSelect?: (event: Event) => void
  textValue?: string
  inset?: boolean
  /**
   * Data attributes:
   * [data-highlighted] - Present when highlighted
   * [data-disabled] - Present when disabled
   * [data-orientation] - "vertical" | "horizontal"
   */
  className?: string
  children: ReactNode
}

export const DropdownMenuItem = forwardRef<
  ElementRef<typeof Item>,
  DropdownMenuItemProps
>(({ className, onSelect, inset, loading, disabled, ...props }, ref) => {
  const isDisabled = disabled || loading

  return (
    <Item
      ref={ref}
      className={cn(
        'relative flex cursor-default select-none items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-hidden transition-colors focus:bg-accent focus:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0',
        inset && 'pl-8',
        loading &&
          'transition-none after:absolute after:inset-0 after:z-10 after:animate-pulse after:rounded-md after:bg-zinc-100 dark:after:bg-zinc-800',
        className
      )}
      disabled={isDisabled}
      onSelect={(event) => {
        if (onSelect) {
          // Without this requestAnimationFrame, all mouse events on the body are disabled. The dropdown menu sets pointer-events-none on the body. If we then immediately open the confirmation dialog, the confirmation dialog will save that state and restore it after it's closed. So we wait a frame for the dropdown menu to unset pointer-events-none.
          requestAnimationFrame(() => onSelect(event))
        }
      }}
      {...props}
    />
  )
})
DropdownMenuItem.displayName = Item.displayName
